// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * Whether a line item's amount agrees with its quantity and unit price, and the
 * number a person would otherwise have to work out to know.
 *
 * ## Why this is not a comparison
 *
 * It was one: `|quantity × unitPrice − amount| <= 0.02`. That is right for an
 * invoice and wrong for a credit memo, where the quantity and unit price are
 * printed positive and the extended amount is a credit — five at ten pounds,
 * amount −50. The magnitudes agree exactly; only the sign differs, because the
 * document is taking money off rather than putting it on. A plain equality reads
 * that as being out by a hundred and sends a correct document to review.
 *
 * A sign disagreement of equal magnitude is therefore reported as a credit
 * rather than as a fault. That is a deliberate loosening and worth stating
 * plainly: it means a genuine sign error on a row is not caught here. The trade
 * is worth it — a credit line is common and a sign typo on an extended amount
 * the reader took off the page is not, and the alternative was every credit memo
 * arriving pre-flagged, which teaches an operator to clear warnings without
 * reading them.
 *
 * ## Why the expected value is returned rather than just a verdict
 *
 * Because the operator cannot do the multiplication. Dustin, looking at a real
 * row: "quantity six unit price six hundred and twelve point forty one amount
 * thirty six seventy four point forty six — I have no idea what six times six
 * hundred and twelve is, so I can't do that without pulling up a calculator."
 * A screen that says a row disagrees, without saying what it should have been,
 * has handed the operator arithmetic homework and called it a review.
 */

/**
 * Every verdict with an `expected` amount also says the discount the row prints, if any
 * (`discountPercent`, 5 for "less 5%"), since `expected` is then quantity × unit price less
 * it, and a sum written without it ("20 × 10.00 = 190.00") reads as wrong arithmetic.
 */
type Discounted = { readonly discountPercent?: number };

export type LineItemArithmetic =
  /** Too little on the row to say anything about it. */
  | { readonly verdict: 'unchecked' }
  /**
   * The amount is missing, but the quantity and unit price give it.
   *
   * Worth its own answer rather than being lumped in with "nothing to check":
   * the product knows what this row comes to, and the operator was retyping a
   * multiplication the screen had already done.
   */
  | ({ readonly verdict: 'computable'; readonly expected: number } & Discounted)
  /** Amount equals quantity × unit price. */
  | ({ readonly verdict: 'balanced'; readonly expected: number } & Discounted)
  /** Amount is the negative of quantity × unit price: a credit, not a fault. */
  | ({ readonly verdict: 'credit'; readonly expected: number } & Discounted)
  /** Amount is neither. `expected` is what quantity × unit price actually comes to. */
  | ({
      readonly verdict: 'off';
      readonly expected: number;
      readonly difference: number;
    } & Discounted);

/** Rounding slack, in currency units. Two printed decimals can disagree by a penny. */
const TOLERANCE = 0.02;

/**
 * Within the slack, allowing for binary floating point. The engine compares in decimal, where
 * 1 × 5.12 against 5.10 is exactly 0.02 and balances; here 5.12 − 5.10 is 0.020000000000000462,
 * which read as off. The allowance grows with the size of the numbers, never past a cent's
 * millionth on any amount a document prints.
 */
const withinTolerance = (difference: number, ...values: number[]): boolean =>
  Math.abs(difference) <= TOLERANCE + 1e-9 + 1e-12 * Math.max(0, ...values.map(Math.abs));

/** .NET's `\w` (letters, non-spacing marks, decimal digits, connectors, and the two joiners `\b` counts). */
const WORD = String.raw`[\p{L}\p{Mn}\p{Nd}\p{Pc}‌‍]`;
/** .NET's `\s`. */
const SPACE = String.raw`[\f\n\r\t\v\x85\p{Z}]`;
const PERCENT = String.raw`[0-9]{1,2}(?:\.[0-9]{1,2})?`;
/**
 * DocumentPipeline.PrintedDiscount, with .NET's `\b`, `\s` and `(?i)` spelled out so both sides
 * read the same text the same way. [0-9], not \d, as there: other scripts' digits are refused.
 */
const PRINTED_DISCOUNT = new RegExp(
  String.raw`(?<!${WORD})less${SPACE}+(${PERCENT})${SPACE}*%|(${PERCENT})${SPACE}*%${SPACE}*(?:discount|off)(?!${WORD})`,
  'giu',
);

/** The one discount percentage a row's text prints (5 for "Less 5% discount"), or null when it prints none, or several. */
export const printedDiscountPercent = (description?: string | null): number | null => {
  if (!description?.trim()) return null;
  const matches = [...description.matchAll(PRINTED_DISCOUNT)];
  if (matches.length !== 1) return null;
  const percent = Number(matches[0][1] ?? matches[0][2]);
  return percent > 0 && percent < 100 ? percent : null;
};

/**
 * 1 minus the one discount percentage a row's text prints ("Less 10% discount", "5% off"), or
 * null when it prints none, or several. DocumentPipeline.PrintedDiscountFactor; the shared cases
 * (tests/shared-rules/line-item-cases.json, "balances" with a description) keep the two together.
 */
export const printedDiscountFactor = (description?: string | null): number | null => {
  const percent = printedDiscountPercent(description);
  return percent === null ? null : 1 - percent / 100;
};

/** Rounded to the cent, halves away from zero, as the engine's decimal Math.Round. */
const roundCents = (value: number): number =>
  (Math.sign(value) * Math.round(Math.abs(value) * 100 + 1e-7)) / 100;

/** Provenance for a blank row explicitly added by the operator, never OCR. */
export const OPERATOR_ENTERED_ROW = 'operator-entry';

/** A saved authored row needs no second approval of the values just typed.
 * Quantity/rate may both be absent for amount-only lines, but a half-entered
 * pair, blank description, missing amount or invalid number is still a draft.
 * This grants no arithmetic exception: an inconsistent total stays flagged.
 */
export const operatorEnteredRowIsComplete = (row: {
  source?: string | null;
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
}): boolean =>
  row.source === OPERATOR_ENTERED_ROW &&
  row.description.trim().length > 0 &&
  row.amount != null &&
  Number.isFinite(row.amount) &&
  ((row.quantity == null && row.unitPrice == null) ||
    (row.quantity != null &&
      row.unitPrice != null &&
      Number.isFinite(row.quantity) &&
      Number.isFinite(row.unitPrice)));

/**
 * The row's numbers, and its description for a discount it prints itself
 * (DocumentPipeline.LineItemBalances with a description).
 *
 * A row that prints its own discount ("Less 5% discount") balances when quantity ×
 * unit price, less that percentage and rounded to the cent, gives the amount, in either
 * sign. One that still balances without the discount contradicts itself (its quantity
 * was completed as amount ÷ price, or its amount is the undiscounted one), so it is off,
 * and `expected` is the discounted amount.
 */
export const lineItemArithmetic = (item: {
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
  description?: string | null;
}): LineItemArithmetic => {
  const { quantity, unitPrice, amount } = item;
  if (
    quantity == null ||
    unitPrice == null ||
    !Number.isFinite(quantity) ||
    !Number.isFinite(unitPrice)
  )
    return { verdict: 'unchecked' };
  const percent = printedDiscountPercent(item.description);
  const discount = percent === null ? null : 1 - percent / 100;
  const expected =
    discount === null ? quantity * unitPrice : roundCents(quantity * unitPrice * discount);
  const discounted = percent === null ? {} : { discountPercent: percent };
  // The two that make the third. Offered rather than filled in: a row the reader
  // could not find an amount for is a row worth an operator's glance, and
  // writing a number nobody has looked at is how a wrong total becomes a
  // confident one.
  if (amount == null || !Number.isFinite(amount))
    return { verdict: 'computable', expected, ...discounted };

  if (withinTolerance(expected - amount, expected, amount))
    return { verdict: 'balanced', expected, ...discounted };
  // Equal magnitude, opposite sign. A credit memo prints its quantity and unit
  // price positive and takes the money off at the end of the row. The engine's
  // discount rule compares magnitudes alone, so a discounted row needs no floor.
  if (
    withinTolerance(expected + amount, expected, amount) &&
    (discount !== null || !withinTolerance(expected, expected))
  )
    return { verdict: 'credit', expected, ...discounted };
  return { verdict: 'off', expected, difference: amount - expected, ...discounted };
};

/** True when the row is arithmetically acceptable — balanced, a credit, or unchecked. */
export const lineItemBalances = (item: {
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
  description?: string | null;
}): boolean => lineItemArithmetic(item).verdict !== 'off';

/** Presentation only. Admission treats unchecked rows as not failing, but that
 * is not proof that they add up. Keep both review surfaces' totals honest without
 * changing stored validation, correction, confirmation or export decisions. */
export const lineItemArithmeticSummary = (
  items: ReadonlyArray<{
    quantity?: number | null;
    unitPrice?: number | null;
    amount?: number | null;
    description?: string | null;
    arithmeticValidated?: boolean;
  }>,
) => {
  const summary = { balanced: 0, accepted: 0, off: 0, missingAmounts: 0 };
  for (const item of items) {
    const verdict = lineItemArithmetic(item).verdict;
    if (verdict === 'balanced' || verdict === 'credit') summary.balanced++;
    else if (verdict === 'off') {
      if (item.arithmeticValidated) summary.accepted++;
      else summary.off++;
    } else if (verdict === 'computable') summary.missingAmounts++;
  }
  return summary;
};

/** A field as the totals check reads it: a currency value the document read. */
type TotalsField = {
  name: string;
  value: string;
  canonicalValue?: string | null;
  fieldKind?: string | null;
};

/**
 * A currency field's value as the engine parses it (NumberStyles.Number, invariant: spaces only around
 * it, thousands separators only in the whole part, one sign leading or trailing); null when absent,
 * unreadable or zero. "3 692.00"
 * is unreadable there, so it is here: stripping inner spaces read it as 3,692 and held a table the
 * engine let through.
 */
const currencyAmount = (field: TotalsField): number | null => {
  const trimmed = (field.canonicalValue ?? field.value ?? '').replace(
    /^[\t\n\v\f\r ]+|[\t\n\v\f\r ]+$/g,
    '',
  );
  // One sign, before the number or after it ("59.80-"), as NumberStyles.Number allows; the sign
  // itself is dropped below, since only the magnitude is compared.
  if (
    !/^(?:[-+]?(?:[0-9][0-9,]*(?:\.[0-9]*)?|\.[0-9]+)|(?:[0-9][0-9,]*(?:\.[0-9]*)?|\.[0-9]+)[-+])$/.test(
      trimmed,
    )
  )
    return null;
  const text = trimmed.replace(/[-+]$/, '').replace(/,/g, '');
  const value = Math.abs(Number(text));
  return Number.isFinite(value) && value !== 0 ? value : null;
};

/** Cents, so sums of printed two-decimal amounts compare exactly. */
const cents = (value: number): number => Math.round(value * 100);

/**
 * Whether the rows add up to the document's own totals: ReviewState.TableMissesItsSubtotal and
 * TableRunsPastItsTotal, the engine's "table-total" reason, answered the same way (line-item-cases.json,
 * "tableTotals", which the engine's tests read too). Every row can balance and the table still miss a line:
 * invoice RIT-INV-26008 (2026-10-05) read four of its five lines, each adding up, 59.80 short of its subtotal,
 * while the chip said "No arithmetic issues". Null when the rows match, when there is nothing to compare (no row
 * with an amount, no subtotal or total read) or once a person verified every row; otherwise the rows' sum and
 * the subtotal (or, without one, the total the first rows already reached).
 */
export const linesMissTheirTotal = (
  allRows: ReadonlyArray<{ amount?: number | null; verified?: boolean }>,
  fields: ReadonlyArray<TotalsField>,
): { sum: number; total: number; subtotal: boolean } | null => {
  if (allRows.length === 0 || allRows.every((row) => row.verified)) return null;
  // A row with no amount (a description split from its numbers) is left out of the sum, not a reason to skip the
  // check: invoice RBS-INV-26037 (2026-10-05) lost a line, split another, and released with this chip clean.
  const rows = allRows.filter((row) => row.amount != null && Number.isFinite(row.amount));
  if (rows.length === 0) return null;
  const currency = fields.filter((field) => (field.fieldKind ?? '').toLowerCase() === 'currency');
  const totals = currency
    .filter((field) => field.name.toLowerCase().includes('total'))
    .map(currencyAmount)
    .filter((value): value is number => value !== null);
  const subtotals = currency
    .filter((field) => field.name.replace(/[ -]/g, '').toLowerCase().includes('subtotal'))
    .map(currencyAmount)
    .filter((value): value is number => value !== null);
  const sum = rows.reduce((total, row) => total + cents(row.amount as number), 0);
  const isA = (amounts: number[], value: number) =>
    amounts.some((total) => Math.abs(Math.abs(value) - cents(total)) <= 1);
  // Short of (or over) the subtotal: the rows add up to neither it nor any total the document reads.
  if (subtotals.length > 0 && !isA([...totals, ...subtotals], sum))
    return { sum: Math.abs(sum) / 100, total: subtotals[0], subtotal: true };
  // Runs on past a total its first rows already reached (closing lines read as items).
  if (rows.length >= 2 && totals.length > 0 && !isA(totals, sum)) {
    let running = 0;
    for (const row of rows.slice(0, -1)) {
      running += cents(row.amount as number);
      const reached = totals.find((total) => Math.abs(Math.abs(running) - cents(total)) <= 1);
      if (reached !== undefined)
        return { sum: Math.abs(sum) / 100, total: reached, subtotal: false };
    }
  }
  return null;
};

/** Empty and absent are one absence: a cleared cell arrives as `null` where the row held `undefined`. */
const sameNumber = (left?: number | null, right?: number | null): boolean =>
  (left ?? null) === (right ?? null);

/**
 * The three numbers an acceptance is about, and the discount the description prints, which changes
 * what they must come to (2 × 10.00 = 200.00 is one row; "less 5%" makes it another). Any other
 * description edit, a typo fixed, cannot change what the acceptance meant.
 */
const sameArithmetic = (
  before: {
    quantity?: number | null;
    unitPrice?: number | null;
    amount?: number | null;
    description?: string | null;
  },
  after: {
    quantity?: number | null;
    unitPrice?: number | null;
    amount?: number | null;
    description?: string | null;
  },
): boolean =>
  sameNumber(before.quantity, after.quantity) &&
  sameNumber(before.unitPrice, after.unitPrice) &&
  sameNumber(before.amount, after.amount) &&
  printedDiscountFactor(before.description) === printedDiscountFactor(after.description);

/**
 * The `arithmeticValidated` a row carries once an operator has finished editing it.
 *
 * ## The two things that flag says, and why one of them is not arithmetic
 *
 * `arithmeticValidated` carries either "the numbers agree" — which anything can
 * derive — or "a person looked at a row that does not add up and said it is right
 * as printed", which nothing can. A folded discount is a real thing: quantity 3,
 * unit price 100.00, amount 250.00 is a correct invoice line and no rule will ever
 * see that. Saying so is a judgement, and `acceptLineItem` is where a person makes
 * it, deliberately, on a button that reads "Looks right".
 *
 * Both edit paths used to write `arithmeticValidated: lineItemBalances(patched)`,
 * which can only ever answer the first question. So an operator who accepted that
 * row and then corrected a typo in its DESCRIPTION had their answer thrown away by
 * an edit that changed no number at all: the row went back to failing, the finding
 * came back, and the document returned to the queue they had just cleared it from
 * — the exact failure `ReviewState.ArithmeticIsOff` and `finding-clearing.ts` were
 * both written to prevent, still live on the one path that overwrites the flag.
 *
 * ## Why the answer is not "stop writing it"
 *
 * Because a correction has to clear the fault, and on the engine's side the flag is
 * the ONLY thing that does. `ReviewState.ArithmeticIsOff` asks whether a row has
 * three numbers and no flag; it never recomputes the multiplication. An operator
 * who fixes the amount to 300.00 and gets no flag written has a row that adds up in
 * front of them and a document the engine holds in review for ever.
 *
 * ## So the rule is one sentence, in two clauses that mean different things
 *
 * The row is settled when **the numbers now agree**, or when **a person accepted
 * these numbers and they are still these numbers**. A judgement belongs to the
 * values it was made about — the same rule this product already applies to a
 * lookup's citation — so changing a number retires the acceptance rather than
 * carrying it onto a row nobody has seen. An accepted 3 × 100.00 = 250.00 whose
 * quantity is then mistyped as 30 is asked about again; it is not vouched for.
 *
 * An edit never GRANTS an acceptance, and never silently withdraws one.
 */
export const arithmeticValidatedAfterEdit = (
  before: {
    quantity?: number | null;
    unitPrice?: number | null;
    amount?: number | null;
    description?: string | null;
    arithmeticValidated?: boolean;
  },
  after: {
    quantity?: number | null;
    unitPrice?: number | null;
    amount?: number | null;
    description?: string | null;
  },
): boolean =>
  lineItemBalances(after) || (Boolean(before.arithmeticValidated) && sameArithmetic(before, after));

/**
 * The multiplication, written out, so nobody reaches for a calculator.
 *
 * Grouped thousands and two decimals, because these are money and a run of
 * digits is exactly what the operator could not read in the first place.
 */
export const money = (value: number): string =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** With the discount the row prints (`LineItemArithmetic.discountPercent`): "20 × 10.00 less 5%", which is 190.00. */
export const lineItemSum = (
  quantity: number,
  unitPrice: number,
  discountPercent?: number | null,
): string =>
  `${quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })} × ${money(unitPrice)}` +
  (discountPercent == null
    ? ''
    : ` less ${discountPercent.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`);

/**
 * The one-column-left misread, and the row it should have been.
 *
 * Measured on a real Accounts Payable batch: of nineteen rows an operator
 * corrected by hand, fifteen were the identical fault. On a wide table the
 * reader takes the unit price for the quantity, the amount for the unit price,
 * and whatever sits to the right of that for the amount:
 *
 *     read     1011.55 / 6069.30 / 6136999.65
 *     printed        6 / 1011.55 /     6069.30
 *
 * The true quantity is not lost with the column — it is the ratio of the two
 * numbers that WERE captured, because the extended amount is quantity times unit
 * price and both survived the shift. On all fifteen rows oldUnitPrice divided by
 * oldQuantity gave exactly the whole number the operator typed.
 *
 * Only offered when that ratio lands on a whole number and the row does not
 * currently balance. A row that already adds up is not misread, and a ratio that
 * is not a count is not a quantity — both would turn a confident guess into a
 * wrong number nobody checked. Nor is it offered for a row someone accepted
 * ("Looks right", or the engine's own check): that answer is about these numbers,
 * and a shift would replace them with ones nobody looked at. A row that prints
 * its own discount is left alone too: 2 × 10.00 less 5% is a correct 19.00, not
 * a misread 5 × 2.00 (the engine's learned shift skips such rows the same way).
 */
export type ShiftedLineItem = {
  readonly quantity: number;
  readonly unitPrice: number;
  readonly amount: number;
};

export const shiftedLineItem = (item: {
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
  description?: string | null;
  arithmeticValidated?: boolean;
}): ShiftedLineItem | null => {
  if (item.arithmeticValidated) return null;
  if (printedDiscountFactor(item.description) !== null) return null;
  const { quantity, unitPrice } = item;
  if (quantity == null || unitPrice == null) return null;
  if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return null;
  if (quantity <= 0 || unitPrice <= 0) return null;
  // Already correct rows are left alone.
  if (lineItemArithmetic(item).verdict !== 'off') return null;
  const count = unitPrice / quantity;
  if (!Number.isFinite(count)) return null;
  // A count that rounds to zero is not a quantity: 5000 × 0.02 = 1.00 would
  // otherwise become 0 × 5000 = 0.02 (the engine's Unshift refuses it too).
  const whole = Math.round(count);
  if (whole <= 0 || Math.abs(count - whole) > 0.001) return null;
  return { quantity: whole, unitPrice: quantity, amount: unitPrice };
};
